import { Activity } from '@la/types';
import { getScheduleServiceXRequestId } from 'lib/auth/auth';
import baseApi from './baseApi';
import {
  ProgramPermissions,
  ProgramPermissionsResponse,
  ScoreUpdateObj,
  UserRsvp,
  UserRsvpResponse,
  RosterResponse,
  UserSummaryResponse,
  Attendance,
  CheckIn,
  CheckInStatus,
} from './types/singleActivity';

export const singleActivityApi = baseApi
  .enhanceEndpoints({
    addTagTypes: ['Attendance', 'SingleActivity', 'CheckIns'],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      getSingleActivity: builder.query<
        Activity,
        { activityId: number; siteId: string | null; subdomain: string }
      >({
        query: ({ siteId, activityId, subdomain }) => {
          return {
            url: `/api/member-portal/${subdomain}/singleActivity`,
            method: 'GET',
            params: {
              activityId,
              siteId,
            },
            headers: {
              'X-Request-ID': getScheduleServiceXRequestId(),
            },
          };
        },
        providesTags: ['SingleActivity'],
      }),
      getUserSummary: builder.query<
        UserSummaryResponse,
        {
          activityId: number;
          siteId: string | null;
          subdomain: string;
          isGame?: boolean;
        }
      >({
        query: ({ siteId, activityId, subdomain, isGame }) => {
          return {
            url: `/api/member-portal/${subdomain}/singleActivity/userSummary`,
            method: 'GET',
            params: {
              activityId,
              siteId,
              isGame,
            },
            headers: {
              'X-Request-ID': getScheduleServiceXRequestId(),
            },
          };
        },
      }),
      getProgramPermissions: builder.query<
        ProgramPermissions,
        { subdomain: string; programId?: number }
      >({
        query: ({ programId, subdomain }) => {
          return {
            url: `/api/member-portal/${subdomain}/singleActivity/permissions`,
            method: 'GET',
            params: {
              programId,
            },
          };
        },
        transformResponse: (response: ProgramPermissionsResponse) => {
          return response.permissions[0];
        },
      }),
      getProgramSettings: builder.query<
        any,
        { subdomain: string; programId?: number }
      >({
        query: ({ programId, subdomain }) => {
          return {
            url: `/api/member-portal/${subdomain}/singleActivity/settings`,
            method: 'GET',
            params: {
              programId,
            },
          };
        },
      }),
      getSingleActivityRoster: builder.query<
        RosterResponse,
        {
          subdomain: string;
          siteId: string;
          teamId: string;
        }
      >({
        query: ({ subdomain, siteId, teamId }) => ({
          url: `/api/member-portal/${subdomain}/singleActivity/roster`,
          params: { siteId, teamId },
        }),
      }),
      getRsvpResponse: builder.query<
        UserRsvp[],
        { activityId?: string; subdomain: string }
      >({
        query: ({ activityId, subdomain }) => {
          return {
            url: `/api/member-portal/${subdomain}/singleActivity/rsvp`,
            method: 'GET',
            params: {
              activityId,
            },
            headers: {
              'X-Request-ID': getScheduleServiceXRequestId(),
            },
          };
        },
      }),
      postRsvpResponse: builder.mutation<
        UserRsvp,
        {
          activityId?: number;
          respondentId: number;
          userRsvpResponse: UserRsvpResponse;
          subdomain: string;
        }
      >({
        query: ({ activityId, respondentId, userRsvpResponse, subdomain }) => {
          return {
            url: `/api/member-portal/${subdomain}/singleActivity/rsvp`,
            method: 'POST',
            body: {
              activityId,
              respondentId,
              userRsvpResponse,
            },
            headers: {
              'X-Request-ID': getScheduleServiceXRequestId(),
            },
          };
        },
        invalidatesTags: ['Attendance'],
      }),
      updateRsvpResponse: builder.mutation<
        UserRsvp,
        {
          activityId?: number;
          respondentId: number;
          userRsvpResponse: UserRsvpResponse;
          subdomain: string;
        }
      >({
        query: ({ activityId, respondentId, userRsvpResponse, subdomain }) => {
          return {
            url: `/api/member-portal/${subdomain}/singleActivity/rsvp`,
            method: 'PATCH',
            body: {
              activityId,
              respondentId,
              userRsvpResponse,
            },
            headers: {
              'X-Request-ID': getScheduleServiceXRequestId(),
            },
          };
        },
        invalidatesTags: ['Attendance'],
      }),
      updateGameScore: builder.mutation<
        any,
        {
          subdomain: string;
          activityId?: string;
          op: string;
          score: ScoreUpdateObj;
          userId: number;
        }
      >({
        query: ({ subdomain, activityId, op, score, userId }) => {
          return {
            url: `/api/member-portal/${subdomain}/singleActivity/score`,
            method: 'PATCH',
            body: {
              activityId,
              userId,
              op,
              score,
            },
            headers: {
              'X-Request-ID': getScheduleServiceXRequestId(),
            },
          };
        },
        invalidatesTags: ['SingleActivity'],
      }),
      getCheckInList: builder.query<
        CheckIn[],
        {
          activityId?: number;
          subdomain: string;
          siteId: string;
          teamIds?: string;
          programId: number;
        }
      >({
        query: ({ activityId, siteId, programId, subdomain, teamIds }) => {
          return {
            url: `/api/member-portal/${subdomain}/singleActivity/checkins`,
            method: 'GET',
            params: {
              activityId,
              siteId,
              programId,
              teamIds,
            },
            headers: {
              'X-Request-ID': getScheduleServiceXRequestId(),
            },
          };
        },
        providesTags: ['CheckIns'],
      }),
      postCheckIn: builder.mutation<
        CheckIn,
        {
          activityId: number;
          status: CheckInStatus;
          subdomain: string;
          userId: number;
        }
      >({
        query: ({ activityId, userId, status, subdomain }) => {
          return {
            url: `/api/member-portal/${subdomain}/singleActivity/checkins`,
            method: 'POST',
            body: {
              activityId,
              status,
              userId,
            },
            headers: {
              'X-Request-ID': getScheduleServiceXRequestId(),
            },
          };
        },
        invalidatesTags: ['CheckIns'],
      }),
      updateCheckIn: builder.mutation<
        CheckIn,
        {
          activityId?: number;
          userId: number;
          status: CheckInStatus;
          subdomain: string;
        }
      >({
        query: ({ activityId, userId, status, subdomain }) => {
          return {
            url: `/api/member-portal/${subdomain}/singleActivity/checkins`,
            method: 'PATCH',
            body: {
              activityId,
              status,
              userId,
            },
            headers: {
              'X-Request-ID': getScheduleServiceXRequestId(),
            },
          };
        },
        invalidatesTags: ['CheckIns'],
      }),
      getAttendance: builder.query<
        Attendance[],
        {
          activityId: string;
          programId: string;
          siteId?: string;
          subdomain: string;
          teamIds?: string;
        }
      >({
        query: ({ activityId, programId, siteId, subdomain, teamIds }) => {
          return {
            url: `/api/member-portal/${subdomain}/singleActivity/attendance`,
            method: 'GET',
            params: {
              activityId,
              programId,
              siteId,
              teamIds,
            },
            headers: {
              'X-Request-ID': getScheduleServiceXRequestId(),
            },
          };
        },
        providesTags: ['Attendance'],
      }),
    }),
  });

export const {
  useGetSingleActivityQuery,
  useGetUserSummaryQuery,
  useGetProgramPermissionsQuery,
  useGetProgramSettingsQuery,
  useGetSingleActivityRosterQuery,
  useGetRsvpResponseQuery,
  useUpdateGameScoreMutation,
  usePostRsvpResponseMutation,
  useUpdateRsvpResponseMutation,
  useGetAttendanceQuery,
  useGetCheckInListQuery,
  usePostCheckInMutation,
  useUpdateCheckInMutation,
} = singleActivityApi;
